import { useFreights } from "@/hooks/useFreights";
import { cn } from "@/lib/utils";

interface FreightSelectorProps {
  companyId: string;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
}

function formatMoney(value: string) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number.parseFloat(value));
}

export function FreightSelector({ companyId, selectedIds, onChange }: FreightSelectorProps) {
  const { data, isLoading } = useFreights(
    companyId
      ? { companyId, status: "COMPLETED", perPage: 100 }
      : { status: "COMPLETED", perPage: 100 },
  );

  const freights = data?.data ?? [];

  function toggle(id: string, alreadyInvoiced: boolean) {
    if (alreadyInvoiced) return;
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((fid) => fid !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  }

  if (!companyId) {
    return (
      <p className="text-sm text-muted-foreground italic">
        Selecione uma empresa para ver os fretes disponíveis.
      </p>
    );
  }

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Carregando fretes…</p>;
  }

  if (freights.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic">
        Nenhum frete concluído encontrado para esta empresa.
      </p>
    );
  }

  return (
    <div className="space-y-1.5 max-h-56 overflow-y-auto rounded-md border border-input">
      {freights.map((freight) => {
        const alreadyInvoiced = !!freight.invoiceId;
        const checked = selectedIds.includes(freight.id);
        return (
          <label
            key={freight.id}
            className={cn(
              "flex items-center gap-3 px-3 py-2.5 text-sm transition-colors cursor-pointer",
              alreadyInvoiced
                ? "opacity-50 cursor-not-allowed bg-muted/30"
                : "hover:bg-muted/40",
              checked && "bg-primary/5",
            )}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={alreadyInvoiced}
              onChange={() => toggle(freight.id, alreadyInvoiced)}
              className="h-4 w-4 rounded border-input accent-primary"
            />
            <span className="flex-1 truncate">
              {freight.originCity}/{freight.originState} → {freight.destinationCity}/{freight.destinationState}
            </span>
            <span className="shrink-0 font-medium text-foreground">
              {formatMoney(freight.freightValue)}
            </span>
            {alreadyInvoiced && (
              <span className="shrink-0 text-xs text-muted-foreground">já faturado</span>
            )}
          </label>
        );
      })}
    </div>
  );
}
